import { Capacitor } from "@capacitor/core";

/**
 * Environment configuration utility
 * Handles three environments:
 * - Local: http://localhost:3000
 * - Testing: https://staging.luminousapp.com
 * - Production: https://luminousapp.com
 */

export type Environment = "local" | "testing" | "production";

const normalizeBaseUrl = (value?: string): string | undefined => {
  if (!value) {
    return undefined;
  }

  const trimmed = value.trim().replace(/\/+$/, "");
  if (!trimmed) {
    return undefined;
  }

  return trimmed.replace(/\/api$/, "");
};

/**
 * Get the current environment based on the hostname
 */
export const getEnvironment = (): Environment => {
  if (typeof window === "undefined") {
    // SSR or non-browser environment - default to production
    return "production";
  }

  const hostname = window.location.hostname;

  // Check for localhost
  if (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.startsWith("192.168.")
  ) {
    return "local";
  }

  // Check for staging environment before production
  if (hostname === "staging.luminousapp.com") {
    return "testing";
  }

  // Check for production environment
  if (hostname.includes("luminousapp.com")) {
    return "production";
  }

  // Default to production if unknown
  return "production";
};

/**
 * Get the API base URL based on the current environment
 */
export const getApiBaseUrl = (): string => {
  if (Capacitor.isNativePlatform()) {
    return "https://luminousapp.com";
  }

  const env = getEnvironment();

  switch (env) {
    case "local":
      return process.env.REACT_APP_API_URL || "http://localhost:5000";
    case "testing":
      return "https://staging.luminousapp.com";
    case "production":
      return typeof window !== "undefined"
        ? window.location.origin
        : "https://luminousapp.com";
    default:
      return typeof window !== "undefined"
        ? window.location.origin
        : "http://localhost:5000";
  }
};

/**
 * Get the frontend base URL (for links, navigation, etc.)
 */
export const getFrontendBaseUrl = (): string => {
  const envOverride = normalizeBaseUrl(process.env.REACT_APP_FRONTEND_URL);
  if (envOverride) {
    return envOverride;
  }

  // For native apps, always use production URL
  if (Capacitor.isNativePlatform()) {
    return "https://luminousapp.com";
  }

  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    const origin = window.location.origin;

    // Check if it's a local development environment
    if (
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname.startsWith("192.168.")
    ) {
      return "http://localhost:3000";
    }

    // Check for staging environment before production
    if (hostname === "staging.luminousapp.com") {
      return "https://staging.luminousapp.com";
    }

    // Check for production environment
    if (hostname.includes("luminousapp.com")) {
      return "https://luminousapp.com";
    }

    // For other cases, return the origin (web deployment)
    return origin;
  }

  // Default to production for SSR or unknown environments
  return "https://luminousapp.com";
};

/**
 * Get the current environment
 */
export const CURRENT_ENV = getEnvironment();

/**
 * Get the API base URL
 */
export const API_BASE_URL = getApiBaseUrl();

/**
 * Get the frontend base URL
 */
export const FRONTEND_BASE_URL = getFrontendBaseUrl();
